import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';

import clsx from 'clsx';
import { minidenticon } from 'minidenticons';
import { useMediaQuery } from 'usehooks-ts';

import { useAccount } from 'shared/hooks/use-web3';
import { EmojiMessage, useEmojiSocket } from 'shared/hooks/useEmojiSocket';
import { isMobileDevice } from 'shared/utils/isMobileDevice';

import ChatIcon from 'assets/chat-emoji-icon.svg?react';
import CloseIcon from 'assets/close-green.svg?react';

interface Props {
  userAddress: `0x${string}`;
  amIHost?: boolean;
  isGameInProgress?: boolean;
  className?: string;
}

type EmojiPanelPosition = { top: number; left: number };

const EMOJI_LIST = ['😂', '🔥', '🥱', '👍', '💀'];

export const ChatAvatar: FC<Props> = ({
  userAddress,
  amIHost = false,
  isGameInProgress = false,
  className = 'w-8 h-8',
}) => {
  const { activeRoomId } = useParams();
  const { address, chainId } = useAccount();
  const isMobile = useMediaQuery('(max-width: 1199px)');

  const [emojiPanelPosition, setEmojiPanelPosition] = useState<EmojiPanelPosition>({
    top: 0,
    left: 0,
  });
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [visibleEmoji, setVisibleEmoji] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [showPanel, setShowPanel] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleIncomingEmoji = (emoji: string) => {
    setVisibleEmoji(emoji);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setVisibleEmoji(null);
    }, 3000);
  };

  const onEmojiReceived = useCallback(
    (msg: EmojiMessage) => {
      if (
        msg.from.toLocaleLowerCase() !== address.toLocaleLowerCase() &&
        address.toLocaleLowerCase() !== userAddress.toLocaleLowerCase()
      ) {
        handleIncomingEmoji(msg.emoji);
      }
    },
    [address, userAddress],
  );

  const { sendEmoji } = useEmojiSocket({
    chainId,
    roomId: activeRoomId,
    onEmojiReceived,
  });

  const svgURI = useMemo(
    () => 'data:image/svg+xml;utf8,' + encodeURIComponent(minidenticon(userAddress)),
    [userAddress],
  );

  useEffect(() => {
    const el = document.createElement('div');
    document.body.appendChild(el);
    setPortalContainer(el);

    return () => {
      document.body.removeChild(el);
    };
  }, []);

  useEffect(() => {
    const updatePosition = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        let top = -8;
        let left = 80;

        if (viewportWidth < 1200) {
          top = rect.top - 16;
          left = rect.left + rect.width - 25;
          if (left < 8) left = 8;
        }

        setEmojiPanelPosition({ top, left });
      }
    };

    updatePosition();

    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
    };
  }, [showPanel]);

  const handleEmojiClick = (emoji: string) => {
    sendEmoji(userAddress, emoji);
    handleIncomingEmoji(emoji);
    setPanelOpen(false);
  };

  useEffect(() => {
    if (isMobile) {
      setShowPanel(true);
      return;
    }

    const chatTimer = setTimeout(() => {
      setShowPanel(true);
    }, 3000);

    return () => clearTimeout(chatTimer);
  }, [isMobile]);

  const emojiPanelContent = (
    <div
      className={clsx(
        'flex items-center gap-1 rounded-full border border-border bg-black p-1 shadow-lg transition-all duration-300 ease-out',
        panelOpen ? 'max-w-[200px] sm:max-w-[225px]' : 'max-w-[34px]',
      )}
    >
      <div className="shrink-0 rounded-full" data-testid="chat-icon-toggler">
        {panelOpen ? (
          <CloseIcon className="h-[24px] w-[24px] text-white" />
        ) : (
          <ChatIcon className="h-[24px] w-[24px] text-white" />
        )}
      </div>

      <div
        className={clsx(
          'h-[24px] w-[2px] flex-shrink-0 rounded-full bg-[#272D63] transition-opacity duration-300 ease-out',
          panelOpen ? 'opacity-100' : 'opacity-0',
        )}
      />

      <div
        className={clsx(
          'flex gap-2 transition-all duration-300 ease-out',
          panelOpen ? 'opacity-100 delay-100' : 'pointer-events-none opacity-0',
        )}
      >
        {EMOJI_LIST.map((emoji) => (
          <button
            key={emoji}
            onClick={() => handleEmojiClick(emoji)}
            className="flex h-[24px] w-[24px] items-center justify-center rounded-full text-xl transition-transform hover:bg-white/10"
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );

  const emojiPanel = (
    <div
      className="absolute z-50 cursor-pointer"
      onMouseEnter={!isMobileDevice() ? () => setPanelOpen(true) : undefined}
      onClick={() => setPanelOpen(!panelOpen)}
      onMouseLeave={!isMobileDevice() ? () => setPanelOpen(false) : undefined}
      style={{ top: emojiPanelPosition.top, left: emojiPanelPosition.left }}
    >
      {emojiPanelContent}
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={clsx('relative flex flex-col rounded-md bg-[#112969]', className)}
    >
      <img
        src={svgURI}
        alt="Avatar"
        className={clsx('h-full w-full rounded-[12px]', visibleEmoji ? 'opacity-0' : 'opacity-100')}
      />
      <div
        className={clsx(
          'pointer-events-none absolute inset-0 flex items-center justify-center text-3xl transition-all duration-500 ease-in-out sm:text-[64px]',
          visibleEmoji ? 'animate-shake opacity-100 motion-reduce:animate-none' : 'opacity-0',
        )}
      >
        {visibleEmoji}
      </div>

      {amIHost && !visibleEmoji && portalContainer && showPanel && isGameInProgress && (
        <>{isMobile ? createPortal(emojiPanel, portalContainer) : emojiPanel}</>
      )}
    </div>
  );
};
